import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiRequest } from "../../api/client";

function normalizePlatformKey(platform) {
  const p = String(platform || "").trim().toLowerCase();
  if (p === "morrisons") return "morrison";
  if (p === "sainsburry") return "sainsbury";
  if (p === "sainsbury" || p === "sainsburys") return "sainsbury";
  if (p === "asda" || p === "tesco" || p === "morrison" || p === "sainsbury") {
    return p;
  }
  return "";
}

function toActowizPlatformQueryParam(platform) {
  const key = normalizePlatformKey(platform);
  if (!key) return "";
  // Actowiz returns "sainsburry" as platform; filtered requests expect same.
  if (key === "sainsbury") return "sainsburry";
  return key;
}

function firstOfferDescription(p) {
  const offers = p?.offers;
  if (!Array.isArray(offers) || offers.length === 0) return "";
  const x = offers[0];
  if (!x || typeof x !== "object") return "";
  return (
    x.description ||
    x.desc ||
    x.longDescription ||
    x.title ||
    x.text ||
    ""
  );
}

function normalizeProvider(p) {
  const images = Array.isArray(p?.all_images)
    ? p.all_images
    : p?.main_image_url
    ? [p.main_image_url]
    : [];
  const offerLine = firstOfferDescription(p);
  return {
    platform: normalizePlatformKey(p?.platform),
    pid: p?.product_id,
    sku: p?.sku || p?.product_id,
    scrape_time: p?.scrape_time || "",
    title: p?.product_name || p?.sku || "",
    price: p?.price != null ? String(p.price) : "",
    packsize: p?.pack_size || "",
    category: p?.category || p?.category_name || p?.department || "",
    subcategory:
      p?.sub_category ||
      p?.subCategory ||
      p?.subcategory ||
      p?.sub_category_name ||
      p?.subcategory_name ||
      "",
    promotion:
      offerLine ||
      p?.promotion ||
      p?.promotional_text ||
      p?.offer_text ||
      p?.promo_text ||
      p?.multi_buy ||
      p?.deal_text ||
      "",
    updated_at:
      p?.updated_at ??
      p?.scraped_at ??
      p?.last_updated ??
      p?.modified_at ??
      p?.scrape_time ??
      (typeof p?.last_updated_time === "number" ? p.last_updated_time : null) ??
      null,
    images,
    description: p?.description || "",
    url:
      p?.url ||
      p?.product_url ||
      p?.productUrl ||
      p?.store_url ||
      p?.storeUrl ||
      p?.link ||
      null,
    average_rating: p?.average_rating,
    review_count: p?.review_count,
  };
}

function parsePrice(value) {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const normalized = String(value).trim().replace(",", ".").replace(/[^0-9.\-]/g, "");
  if (!normalized) return null;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function buildComparison(providers) {
  const order = ["tesco", "morrison", "asda", "sainsbury"];
  const byPlatform = {};

  (Array.isArray(providers) ? providers : []).forEach((provider) => {
    const platform = normalizePlatformKey(provider?.platform);
    if (!platform) return;
    const price = parsePrice(provider?.price);
    const url =
      provider?.url ||
      provider?.product_url ||
      provider?.productUrl ||
      provider?.store_url ||
      provider?.storeUrl ||
      provider?.link ||
      null;
    const averageRatingRaw =
      provider?.average_rating ?? provider?.avg_rating ?? provider?.rating ?? provider?.review_rating;
    const averageRating = parsePrice(averageRatingRaw);
    const reviewCount = Number.parseInt(
      String(
        provider?.review_count ??
          provider?.reviews ??
          provider?.rating_count ??
          provider?.reviewCount ??
          ""
      ).replace(/[^0-9]/g, ""),
      10
    );
    const prev = byPlatform[platform];

    if (Number.isFinite(price)) {
      if (!prev || !Number.isFinite(prev?.price) || price < prev.price) {
        byPlatform[platform] = {
          price,
          url,
          average_rating: Number.isFinite(averageRating) ? averageRating : null,
          review_count: Number.isFinite(reviewCount) ? reviewCount : null,
        };
      } else if (!prev?.url && url) {
        byPlatform[platform] = { ...prev, url };
      } else if (prev?.average_rating == null && Number.isFinite(averageRating)) {
        byPlatform[platform] = { ...prev, average_rating: averageRating };
      } else if (prev?.review_count == null && Number.isFinite(reviewCount)) {
        byPlatform[platform] = { ...prev, review_count: reviewCount };
      }
      return;
    }

    if (!prev && (url || Number.isFinite(averageRating) || Number.isFinite(reviewCount))) {
      byPlatform[platform] = {
        price: null,
        url,
        average_rating: Number.isFinite(averageRating) ? averageRating : null,
        review_count: Number.isFinite(reviewCount) ? reviewCount : null,
      };
    }
  });

  return order.map((platform) => ({
    platform,
    price: Number.isFinite(byPlatform?.[platform]?.price) ? byPlatform[platform].price : null,
    url: byPlatform?.[platform]?.url || null,
    average_rating: Number.isFinite(byPlatform?.[platform]?.average_rating)
      ? byPlatform[platform].average_rating
      : null,
    review_count: Number.isFinite(byPlatform?.[platform]?.review_count)
      ? byPlatform[platform].review_count
      : null,
  }));
}

function getSearchRequestKey(query, start = 0, offset = 10, limit = offset) {
  const q = String(query || "").trim().toLowerCase();
  return `search:${encodeURIComponent(q)}:${start}:${offset}:${limit}`;
}

function pickBestProvider(providers) {
  if (!Array.isArray(providers) || providers.length === 0) return null;
  let best = providers[0];
  let bestPrice = parsePrice(best?.price);

  for (const p of providers) {
    const pr = parsePrice(p?.price);
    if (Number.isFinite(pr) && (!Number.isFinite(bestPrice) || pr < bestPrice)) {
      best = p;
      bestPrice = pr;
    }
  }
  return best;
}

function mergeCategoryTrees(trees) {
  const map = new Map(); // category -> Set(subcategories)
  (Array.isArray(trees) ? trees : []).forEach((node) => {
    const category = String(node?.category || "").trim();
    if (!category) return;
    if (!map.has(category)) map.set(category, new Set());
    const set = map.get(category);
    (Array.isArray(node?.subcategories) ? node.subcategories : []).forEach((sc) => {
      const v = String(sc || "").trim();
      if (v) set.add(v);
    });
  });
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([category, subSet]) => ({
      category,
      subcategories: Array.from(subSet).sort((a, b) => a.localeCompare(b)),
    }));
}

export const fetchActowizSearch = createAsyncThunk(
  "actowiz/fetchSearch",
  async (
    { query, start = 0, offset = 10, limit, mode = "replace" } = {},
    thunkApi
  ) => {
    const pageLimit = typeof limit === "number" ? limit : offset;
    const qs = new URLSearchParams({
      query: String(query || ""),
      start: String(start ?? 0),
      offset: String(offset ?? 10),
    });

    const res = await apiRequest(`api/actowiz/search?${qs.toString()}`, {
      method: "GET",
    });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") || res.data?.message || "Failed to load search results."
      );
    }

    const results = Array.isArray(res.data?.results)
      ? res.data.results
      : Array.isArray(res.data?.data)
      ? res.data.data
      : Array.isArray(res.data)
      ? res.data
      : [];
    // Actowiz may return total in different keys; handle common variants.
    const total =
      typeof res.data?.total === "number"
        ? res.data.total
        : typeof res.data?.count === "number"
        ? res.data.count
        : typeof res.data?.records === "number"
        ? res.data.records
        : typeof res.data?.num_results === "number"
        ? res.data.num_results
        : null;

    return {
      query,
      start,
      offset,
      mode,
      limit: pageLimit,
      total,
      items: results
        .map((r) => {
          const providers = Array.isArray(r?.providers) ? r.providers : null;
          const best = providers ? pickBestProvider(providers) : null;
          const providerLike = best || r;
          return normalizeProvider(providerLike);
        })
        .filter((x) => x && x.title)
        .slice(0, pageLimit),
    };
  },
  {
    condition: (
      { query, start = 0, offset = 10, limit, force } = {},
      { getState }
    ) => {
      if (!String(query || "").trim()) return false;
      if (force) return true;
      const pageLimit = typeof limit === "number" ? limit : offset;
      const key = getSearchRequestKey(query, start, offset, pageLimit);
      const s = getState()?.actowiz?.requests?.[key];
      if (s === "loading" || s === "succeeded") return false;
      return true;
    },
  }
);

export const fetchActowizProducts = createAsyncThunk(
  "actowiz/fetchProducts",
  async (
    { platform, platform_list, category, sub_category, start = 0, offset = 10, mode = "append" } = {},
    thunkApi
  ) => {
    const normalizeListParam = (value) => {
      if (Array.isArray(value)) {
        return value
          .map((x) => String(x || "").trim())
          .filter(Boolean)
          .join(",");
      }
      return String(value || "").trim();
    };
    const normalizedPlatform = normalizePlatformKey(platform);
    const platformQuery = toActowizPlatformQueryParam(platform);
    const qs = new URLSearchParams();
    const platformListRaw =
      Array.isArray(platform_list) ? platform_list : String(platform_list || "");
    const platformList =
      Array.isArray(platformListRaw)
        ? platformListRaw
            .map((x) => toActowizPlatformQueryParam(x))
            .filter(Boolean)
            .join(",")
        : platformListRaw;

    // New API expects `platform_list`, but keep backward compat with `platform`.
    if (platformList) qs.set("platform_list", platformList);
    else if (platformQuery) qs.set("platform", platformQuery);

    const categoryParam = normalizeListParam(category);
    const subCategoryParam = normalizeListParam(sub_category);
    if (categoryParam) qs.set("category", categoryParam);
    if (subCategoryParam) qs.set("sub_category", subCategoryParam);
    qs.set("start", String(start ?? 0));
    qs.set("offset", String(offset ?? 10));
    const res = await apiRequest(`api/actowiz/products?${qs.toString()}`, {
      method: "GET",
    });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") || res.data?.message || "Failed to load products."
      );
    }

    // Actowiz payloads come in multiple shapes depending on endpoint/version:
    // - { results: [ { providers: [...] }, ... ] }
    // - { products: [ <provider-like product>, ... ] }
    // - [ { platform, results: [ <provider-like product>, ... ] }, ... ]
    // - [ { platform, products: [ <provider-like product>, ... ] }, ... ]
    // - [ <provider-like product>, ... ]
    // - [ { total_count, page, per_page, platform, categories: [...], products: [...] } ]  (admin v2)
    const data = res.data;
    const catalogLike =
      Array.isArray(data) &&
      data.length === 1 &&
      data[0] &&
      typeof data[0] === "object" &&
      Array.isArray(data[0]?.products) &&
      Array.isArray(data[0]?.categories)
        ? data[0]
        : data && typeof data === "object" && Array.isArray(data?.products) && Array.isArray(data?.categories)
        ? data
        : null;

    const totalCount =
      typeof catalogLike?.total_count === "number"
        ? catalogLike.total_count
        : typeof data?.total_count === "number"
        ? data.total_count
        : Array.isArray(data) && data.length > 0 && typeof data[0]?.total_count === "number"
        ? data.reduce((sum, x) => sum + (typeof x?.total_count === "number" ? x.total_count : 0), 0)
        : null;

    const categories = (() => {
      if (Array.isArray(catalogLike?.categories) && catalogLike.categories.length > 0) {
        return catalogLike.categories;
      }
      if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0]?.categories)) {
        const all = data.flatMap((x) => (Array.isArray(x?.categories) ? x.categories : []));
        const merged = mergeCategoryTrees(all);
        return merged.length ? merged : null;
      }
      return null;
    })();

    const results = Array.isArray(data?.results)
      ? data.results
      : Array.isArray(data?.products)
      ? data.products
      : Array.isArray(data)
      ? data
      : [];

    const providers = (() => {
      // Shape: [ { platform, results: [...] }, ... ]
      if (Array.isArray(results) && results.length && Array.isArray(results[0]?.results)) {
        return results.flatMap((group) => (Array.isArray(group?.results) ? group.results : []));
      }
      // Shape: [ { platform, products: [...] }, ... ]
      if (Array.isArray(results) && results.length && Array.isArray(results[0]?.products)) {
        return results.flatMap((group) => (Array.isArray(group?.products) ? group.products : []));
      }
      // Shape: [ { providers: [...] }, ... ]
      if (Array.isArray(results) && results.length && Array.isArray(results[0]?.providers)) {
        return results.flatMap((r) => (Array.isArray(r?.providers) ? r.providers : []));
      }
      // Shape: [ { total_count, categories, products }, ... ] (admin v2)
      if (Array.isArray(results) && results.length && Array.isArray(results[0]?.products)) {
        return results.flatMap((r) => (Array.isArray(r?.products) ? r.products : []));
      }
      // Shape: [ <provider-like product>, ... ]
      return results;
    })();

    const batchLastUpdated =
      Array.isArray(data) &&
      data.length > 0 &&
      typeof data[0]?.last_updated_time === "number"
        ? data[0].last_updated_time
        : null;

    const normalizedItems = providers.map((pr) =>
      normalizeProvider(
        batchLastUpdated != null ? { ...pr, last_updated_time: batchLastUpdated } : pr
      )
    );

    return {
      platform: normalizedPlatform,
      start,
      offset,
      mode,
      totalCount,
      categories,
      items: normalizedItems.filter((x) => x.title),
    };
  },
  {
    condition: (
      { platform, platform_list, category, sub_category, start = 0, offset = 10, force } = {},
      { getState }
    ) => {
      if (force) return true;
      const normalizedPlatform = normalizePlatformKey(platform);
      const platListKey = Array.isArray(platform_list)
        ? platform_list.join(",")
        : String(platform_list || "");
      const catKey = String(category || "").trim().toLowerCase();
      const subKey = String(sub_category || "").trim().toLowerCase();
      const key = `${normalizedPlatform || platListKey || "all"}:${encodeURIComponent(catKey)}:${encodeURIComponent(
        subKey
      )}:${start}:${offset}`;
      const s = getState()?.actowiz?.requests?.[key];
      if (s === "loading" || s === "succeeded") return false;
      return true;
    },
  }
);

export const fetchActowizFeatures = createAsyncThunk(
  "actowiz/fetchFeatures",
  async ({ start = 0, offset = 10, mode = "append" } = {}, thunkApi) => {
    const qs = new URLSearchParams({
      start: String(start ?? 0),
      offset: String(offset ?? 10),
    });
    const res = await apiRequest(`api/actowiz/features?${qs.toString()}`, {
      method: "GET",
    });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") || res.data?.message || "Failed to load features."
      );
    }

    const results = Array.isArray(res.data?.results)
      ? res.data.results
      : Array.isArray(res.data?.data)
      ? res.data.data
      : Array.isArray(res.data)
      ? res.data
      : [];

    // Important: features `offset` is based on `results` records (SKUs),
    // but each result can have many `providers` (one per retailer).
    // UI should render 1 card per `result`, otherwise card count != pagination.
    return {
      start,
      offset,
      mode,
      items: results
        .map((r) => {
          const providers = Array.isArray(r?.providers)
            ? r.providers
            : r && typeof r === "object"
            ? [r]
            : [];
          const normalizedProviders = providers.map((provider) =>
            normalizeProvider({
              ...provider,
              sku: provider?.sku ?? r?.sku ?? provider?.product_id,
            })
          );
          const best = pickBestProvider(normalizedProviders);
          if (!best) return null;
          return {
            ...best,
            comparison: buildComparison(normalizedProviders),
          };
        })
        .filter((x) => x && x.title)
        .slice(0, offset),
    };
  },
  {
    condition: ({ start = 0, offset = 10, force } = {}, { getState }) => {
      if (force) return true;
      const key = `features_v2:${start}:${offset}`;
      const s = getState()?.actowiz?.requests?.[key];
      if (s === "loading" || s === "succeeded") return false;
      return true;
    },
  }
);

export const fetchActowizTopDeals = createAsyncThunk(
  "actowiz/fetchTopDeals",
  async ({ category_list, start = 0, offset = 10, mode = "append" } = {}, thunkApi) => {
    const qs = new URLSearchParams({
      start: String(start ?? 0),
      offset: String(offset ?? 10),
    });
    if (String(category_list || "").trim()) qs.set("category_list", String(category_list));

    const res = await apiRequest(`api/actowiz/top_deals?${qs.toString()}`, {
      method: "GET",
    });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") || res.data?.message || "Failed to load top deals."
      );
    }

    const categories = Array.isArray(res.data) ? res.data : [];
    const providers = categories.flatMap((c) =>
      Array.isArray(c?.providers) ? c.providers : []
    );

    const groupedBySku = new Map();
    providers.forEach((provider) => {
      const normalized = normalizeProvider(provider);
      const key = normalized.pid || normalized.sku || "";
      if (!key || !normalized.title) return;
      if (!groupedBySku.has(key)) groupedBySku.set(key, []);
      groupedBySku.get(key).push(normalized);
    });

    const items = Array.from(groupedBySku.values())
      .map((group) => {
        const best = pickBestProvider(group);
        if (!best) return null;
        return {
          ...best,
          comparison: buildComparison(group),
        };
      })
      .filter((x) => x && x.title)
      .slice(0, offset);

    return {
      category_list: category_list || "",
      start,
      offset,
      mode,
      items,
    };
  },
  {
    condition: ({ category_list, start = 0, offset = 10, force } = {}, { getState }) => {
      if (force) return true;
      const catKey = String(category_list || "").trim().toLowerCase();
      const key = `top_deals:${encodeURIComponent(catKey)}:${start}:${offset}`;
      const s = getState()?.actowiz?.requests?.[key];
      if (s === "loading" || s === "succeeded") return false;
      return true;
    },
  }
);

const initialState = {
  byPlatform: {
    tesco: [],
    asda: [],
    morrison: [],
    sainsbury: [],
  },
  platformCatalog: {
    // platform -> { totalCount: number|null, categories: Array<{category: string, subcategories: string[]}>|null }
  },
  allCatalog: {
    totalCount: null,
    categories: null,
  },
  /** Flat list from `GET actowiz/products` with no `platform` query (used by Quick Compare “Base”). */
  unfilteredProducts: [],
  features: [],
  featuresStatus: "idle", // idle | loading | succeeded | failed
  featuresError: "",
  topDeals: [],
  topDealsStatus: "idle", // idle | loading | succeeded | failed
  topDealsError: "",
  search: [],
  searchStatus: "idle", // idle | loading | succeeded | failed
  searchError: "",
  searchTotal: null,
  statusByPlatform: {
    tesco: "idle",
    asda: "idle",
    morrison: "idle",
    sainsbury: "idle",
  },
  errorByPlatform: {
    tesco: "",
    asda: "",
    morrison: "",
    sainsbury: "",
  },
  // track pagination requests to avoid duplicate calls
  requests: {},
};

const actowizSlice = createSlice({
  name: "actowiz",
  initialState,
  reducers: {
    clearActowiz(state) {
      state.byPlatform = { tesco: [], asda: [], morrison: [], sainsbury: [] };
      state.platformCatalog = {};
      state.allCatalog = { totalCount: null, categories: null };
      state.unfilteredProducts = [];
      state.features = [];
      state.featuresStatus = "idle";
      state.featuresError = "";
      state.topDeals = [];
      state.topDealsStatus = "idle";
      state.topDealsError = "";
      state.search = [];
      state.searchStatus = "idle";
      state.searchError = "";
      state.searchTotal = null;
      state.statusByPlatform = {
        tesco: "idle",
        asda: "idle",
        morrison: "idle",
        sainsbury: "idle",
      };
      state.errorByPlatform = { tesco: "", asda: "", morrison: "", sainsbury: "" };
      state.requests = {};
    },
    clearActowizFeatures(state) {
      state.features = [];
      state.featuresStatus = "idle";
      state.featuresError = "";
      // Clear only features pagination request markers
      Object.keys(state.requests).forEach((k) => {
        if (k.startsWith("features_v2:")) delete state.requests[k];
      });
    },
    clearActowizRequest(state, action) {
      const key = String(action.payload || "");
      if (key) delete state.requests[key];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActowizProducts.pending, (state, action) => {
        const { platform, platform_list, category, sub_category, start = 0, offset = 10 } =
          action.meta.arg || {};
        const normalizedPlatform = normalizePlatformKey(platform);
        const platListKey = Array.isArray(platform_list)
          ? platform_list.join(",")
          : String(platform_list || "");
        const catKey = String(category || "").trim().toLowerCase();
        const subKey = String(sub_category || "").trim().toLowerCase();
        const key = `${normalizedPlatform || platListKey || "all"}:${encodeURIComponent(catKey)}:${encodeURIComponent(
          subKey
        )}:${start}:${offset}`;
        state.requests[key] = "loading";
        if (normalizedPlatform) {
          state.statusByPlatform[normalizedPlatform] = "loading";
          state.errorByPlatform[normalizedPlatform] = "";
        } else {
          Object.keys(state.statusByPlatform).forEach((k) => {
            state.statusByPlatform[k] = "loading";
            state.errorByPlatform[k] = "";
          });
        }
      })
      .addCase(fetchActowizProducts.fulfilled, (state, action) => {
        const { platform, start = 0, offset = 10, items, mode, totalCount, categories } =
          action.payload || {};
        const {
          platform_list: platformListArg,
          category: categoryArg,
          sub_category: subCategoryArg,
        } = action.meta.arg || {};
        const platListKey = Array.isArray(platformListArg)
          ? platformListArg.join(",")
          : String(platformListArg || "");
        const catKey = String(categoryArg || "").trim().toLowerCase();
        const subKey = String(subCategoryArg || "").trim().toLowerCase();
        const key = `${platform || platListKey || "all"}:${encodeURIComponent(catKey)}:${encodeURIComponent(
          subKey
        )}:${start}:${offset}`;
        state.requests[key] = "succeeded";
        if (platform) {
          state.statusByPlatform[platform] = "succeeded";
          state.errorByPlatform[platform] = "";
          const catArgStr = Array.isArray(categoryArg)
            ? categoryArg.join(",")
            : String(categoryArg || "");
          const subArgStr = Array.isArray(subCategoryArg)
            ? subCategoryArg.join(",")
            : String(subCategoryArg || "");
          const hasFilters = Boolean(catArgStr.trim() || subArgStr.trim());

          // Important:
          // Filtered product calls return a *narrowed* `categories` list (only selected categories),
          // so we should NOT overwrite the master category tree used for the Filters UI.
          // Only refresh the master catalog on unfiltered calls (or if we don't have it yet).
          const existing = state.platformCatalog?.[platform] || null;
          const canUpdateCatalog =
            !hasFilters || !existing || !Array.isArray(existing?.categories) || existing.categories.length === 0;

          if (canUpdateCatalog && (totalCount != null || categories != null)) {
            state.platformCatalog[platform] = {
              totalCount:
                typeof totalCount === "number"
                  ? totalCount
                  : existing?.totalCount ?? null,
              categories:
                Array.isArray(categories) && categories.length > 0
                  ? categories
                  : existing?.categories ?? null,
            };
          } else if (!hasFilters && totalCount != null && existing) {
            // Keep totalCount updated on unfiltered calls even if categories missing.
            existing.totalCount = typeof totalCount === "number" ? totalCount : existing.totalCount ?? null;
          }
          if (mode === "replace") {
            state.byPlatform[platform] = items || [];
          } else {
            // Append mode
            if (start === 0) state.byPlatform[platform] = items || [];
            else state.byPlatform[platform] = [...(state.byPlatform[platform] || []), ...(items || [])];
          }
        } else {
          if (totalCount != null || categories != null) {
            state.allCatalog = {
              totalCount: typeof totalCount === "number" ? totalCount : state.allCatalog?.totalCount ?? null,
              categories: Array.isArray(categories) ? categories : state.allCatalog?.categories ?? null,
            };
          }
          if (mode === "replace") {
            state.unfilteredProducts = items || [];
          } else if (start === 0) {
            state.unfilteredProducts = items || [];
          } else {
            state.unfilteredProducts = [...(state.unfilteredProducts || []), ...(items || [])];
          }
          const grouped = { tesco: [], asda: [], morrison: [], sainsbury: [] };
          (items || []).forEach((item) => {
            const p = normalizePlatformKey(item?.platform);
            if (p) grouped[p].push(item);
          });
          Object.keys(grouped).forEach((k) => {
            state.statusByPlatform[k] = "succeeded";
            state.errorByPlatform[k] = "";
            if (mode === "replace") state.byPlatform[k] = grouped[k];
            else if (start === 0) state.byPlatform[k] = grouped[k];
            else state.byPlatform[k] = [...(state.byPlatform[k] || []), ...grouped[k]];
          });
        }
      })
      .addCase(fetchActowizProducts.rejected, (state, action) => {
        const { platform, platform_list, category, sub_category, start = 0, offset = 10 } =
          action.meta.arg || {};
        const normalizedPlatform = normalizePlatformKey(platform);
        const platListKey = Array.isArray(platform_list)
          ? platform_list.join(",")
          : String(platform_list || "");
        const catKey = String(category || "").trim().toLowerCase();
        const subKey = String(sub_category || "").trim().toLowerCase();
        const key = `${normalizedPlatform || platListKey || "all"}:${encodeURIComponent(catKey)}:${encodeURIComponent(
          subKey
        )}:${start}:${offset}`;
        state.requests[key] = "failed";
        if (normalizedPlatform) {
          state.statusByPlatform[normalizedPlatform] = "failed";
          state.errorByPlatform[normalizedPlatform] =
            typeof action.payload === "string" ? action.payload : "Failed to load products.";
        } else {
          Object.keys(state.statusByPlatform).forEach((k) => {
            state.statusByPlatform[k] = "failed";
            state.errorByPlatform[k] =
              typeof action.payload === "string" ? action.payload : "Failed to load products.";
          });
        }
      })
      .addCase(fetchActowizFeatures.pending, (state, action) => {
        const { start = 0, offset = 10 } = action.meta.arg || {};
        const key = `features_v2:${start}:${offset}`;
        state.requests[key] = "loading";
        state.featuresStatus = "loading";
        state.featuresError = "";
      })
      .addCase(fetchActowizFeatures.fulfilled, (state, action) => {
        const { start = 0, offset = 10, items, mode } = action.payload || {};
        const key = `features_v2:${start}:${offset}`;
        state.requests[key] = "succeeded";
        state.featuresStatus = "succeeded";
        state.featuresError = "";
        if (mode === "replace") state.features = items || [];
        else {
          if (start === 0) state.features = items || [];
          else state.features = [...(state.features || []), ...(items || [])];
        }
      })
      .addCase(fetchActowizFeatures.rejected, (state, action) => {
        const { start = 0, offset = 10 } = action.meta.arg || {};
        const key = `features_v2:${start}:${offset}`;
        state.requests[key] = "failed";
        state.featuresStatus = "failed";
        state.featuresError =
          typeof action.payload === "string" ? action.payload : "Failed to load features.";
      });

    builder
      .addCase(fetchActowizTopDeals.pending, (state, action) => {
        const { category_list, start = 0, offset = 10 } = action.meta.arg || {};
        const catKey = String(category_list || "").trim().toLowerCase();
        const key = `top_deals:${encodeURIComponent(catKey)}:${start}:${offset}`;
        state.requests[key] = "loading";
        state.topDealsStatus = "loading";
        state.topDealsError = "";
      })
      .addCase(fetchActowizTopDeals.fulfilled, (state, action) => {
        const { category_list, start = 0, offset = 10, items, mode } = action.payload || {};
        const catKey = String(category_list || "").trim().toLowerCase();
        const key = `top_deals:${encodeURIComponent(catKey)}:${start}:${offset}`;
        state.requests[key] = "succeeded";
        state.topDealsStatus = "succeeded";
        state.topDealsError = "";
        if (mode === "replace") state.topDeals = items || [];
        else {
          if (start === 0) state.topDeals = items || [];
          else state.topDeals = [...(state.topDeals || []), ...(items || [])];
        }
      })
      .addCase(fetchActowizTopDeals.rejected, (state, action) => {
        const { category_list, start = 0, offset = 10 } = action.meta.arg || {};
        const catKey = String(category_list || "").trim().toLowerCase();
        const key = `top_deals:${encodeURIComponent(catKey)}:${start}:${offset}`;
        state.requests[key] = "failed";
        state.topDealsStatus = "failed";
        state.topDealsError =
          typeof action.payload === "string" ? action.payload : "Failed to load top deals.";
      });

    builder
      .addCase(fetchActowizSearch.pending, (state, action) => {
        const { query, start = 0, offset = 10, limit } = action.meta.arg || {};
        const pageLimit = typeof limit === "number" ? limit : offset;
        const key = getSearchRequestKey(query, start, offset, pageLimit);
        state.requests[key] = "loading";
        state.searchStatus = "loading";
        state.searchError = "";
        state.searchTotal = null;
      })
      .addCase(fetchActowizSearch.fulfilled, (state, action) => {
        const {
          start = 0,
          offset = 10,
          mode,
          items,
          query,
          total,
          limit: payloadLimit,
        } = action.payload || {};
        const pageLimit =
          typeof payloadLimit === "number" ? payloadLimit : offset;
        const key = getSearchRequestKey(query, start, offset, pageLimit);
        state.requests[key] = "succeeded";
        state.searchStatus = "succeeded";
        state.searchError = "";
        state.searchTotal = typeof total === "number" ? total : state.searchTotal;
        if (mode === "replace") state.search = items || [];
        else {
          if (start === 0) state.search = items || [];
          else state.search = [...(state.search || []), ...(items || [])];
        }
      })
      .addCase(fetchActowizSearch.rejected, (state, action) => {
        const { query, start = 0, offset = 10, limit } = action.meta.arg || {};
        const pageLimit = typeof limit === "number" ? limit : offset;
        const key = getSearchRequestKey(query, start, offset, pageLimit);
        state.requests[key] = "failed";
        state.searchStatus = "failed";
        state.searchError =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to load search results.";
      });
  },
});

export const { clearActowiz, clearActowizFeatures, clearActowizRequest } = actowizSlice.actions;
export default actowizSlice.reducer;

